/**
 * Personal Progress Mode ("Zen Mode")
 * 
 * Hides all competitive elements for users who just want to track their own progress.
 * Toggled from Settings. Affects:
 * - Hides leaderboard tab (or replaces with "My Progress")
 * - Hides rank/percentile on score reveal and profile
 * - Hides duel notifications
 * - Shows only personal score history, PRs, badges
 * 
 * Stored in profiles.personal_mode (boolean)
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import { supabase } from './supabase';
import { createLogger } from './logger';

const log = createLogger('PERSONAL_MODE');
const STORAGE_KEY = 'duel:personal_mode';

/**
 * Check if personal mode is enabled.
 */
export async function isPersonalModeEnabled(userId?: string): Promise<boolean> {
  // Check local first (for fast UI response)
  try {
    const local = await AsyncStorage.getItem(STORAGE_KEY);
    if (local !== null) return local === 'true';
  } catch {}

  // Check server
  if (userId) {
    try {
      const { data } = await supabase
        .from('profiles')
        .select('personal_mode')
        .eq('id', userId)
        .maybeSingle();
      const enabled = data?.personal_mode === true;
      await AsyncStorage.setItem(STORAGE_KEY, String(enabled));
      return enabled;
    } catch {}
  }

  return false;
}

/**
 * Toggle personal mode on/off.
 */
export async function setPersonalMode(userId: string, enabled: boolean): Promise<void> {
  await AsyncStorage.setItem(STORAGE_KEY, String(enabled));

  try {
    await supabase
      .from('profiles')
      .update({ personal_mode: enabled })
      .eq('id', userId);
    log.info('Personal mode set:', enabled);
  } catch {
    log.warn('Failed to sync personal_mode to server');
  }
}

/**
 * Get the features that should be hidden/modified in personal mode.
 */
export interface PersonalModeConfig {
  hideLeaderboard: boolean;
  hideRank: boolean;
  hidePercentile: boolean;
  hideDuelNotifications: boolean;
  hideGymBattle: boolean;
  showProgressTab: boolean;
}

export function getPersonalModeConfig(enabled: boolean): PersonalModeConfig {
  if (!enabled) {
    return {
      hideLeaderboard: false,
      hideRank: false,
      hidePercentile: false,
      hideDuelNotifications: false,
      hideGymBattle: false,
      showProgressTab: false,
    };
  }
  return {
    hideLeaderboard: true,
    hideRank: true,
    hidePercentile: true,
    hideDuelNotifications: true,
    hideGymBattle: true,
    showProgressTab: true,
  };
}
